<template>
	<section class="event-possible-rewards-popup">
		<div class="possible-rewards-navigation">
			<div
				class="possible-rewards-navigation-button"
				@click="switchChest('previous')"
			>
				<div class="switch-arrow switch-arrow--left arrow--left-22" />
			</div>

			<div
				class="event-market-box-icon"
				:class="[
					`icon-${attributes.type}-market-chest-${currentChest}-100`,
				]"
			/>
			<div
				class="possible-rewards-navigation-button"
				@click="switchChest('next')"
			>
				<div class="switch-arrow switch-arrow--right arrow--right-22" />
			</div>
		</div>
		<div
			class="possible-rewards-title"
			:class="`possible-rewards-title-${currentChest}`"
		>
			<div class="possible-rewards-title-main">
				{{ translate(`event.${currentChest}_chest`) }}
			</div>
			<div class="possible-rewards-title-sub">
				{{
					translate(`event.2to4_rewards`).replace(
						"{2to4}",
						`${getCurrentChestConfig?.dropsPerOpen}`,
					)
				}}
			</div>
		</div>
		<div class="possible-rewards-info">
			{{ translate("event.drop_rate_within_category") }}
		</div>
		<div class="possible-rewards-drop-title">
			<div class="possible-rewards-drop-title-divider">
				<HorizontalDecorDividerFaded direction="left" :is-dark="true" />
			</div>
			<div class="possible-rewards-drop-title-text">
				{{ translate("event.drop_rates") }}
			</div>
		</div>
		<ScrollFade>
			<template #scrollContent>
				<div
					class="possible-rewards-drop-rates"
					v-if="getCurrentChestConfig"
				>
					<EventMarketDropRateBox
						:rewards="getCurrentChestConfig?.pools.common"
						:weight="getCurrentChestConfig?.poolWeights.common"
						:drop-type="MarketDropRateType.COMMON"
					/>
					<EventMarketDropRateBox
						:rewards="getCurrentChestConfig?.pools.uncommon"
						:weight="getCurrentChestConfig?.poolWeights.uncommon"
						:drop-type="MarketDropRateType.UNCOMMON"
					/>
					<EventMarketDropRateBox
						:rewards="getCurrentChestConfig?.pools.rare"
						:weight="getCurrentChestConfig?.poolWeights.rare"
						:drop-type="MarketDropRateType.RARE"
					/>
				</div>
			</template>
		</ScrollFade>
	</section>
</template>

<script lang="ts" setup>
import { useEventStore } from "@/event";
import { MarketChestType, MarketDropRateType } from "@/types/event";
import type { EventGrandPrizeTierConfig } from "@/types/index.ts";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import ScrollFade from "../ScrollFade.vue";
import HorizontalDecorDividerFaded from "../SVG/HorizontalDecorDividerFaded.vue";
import EventMarketDropRateBox from "./EventMarketDropRateBox.vue";

const eventStore = useEventStore();
const { attributes } = storeToRefs(eventStore);

interface Props {
	chestType: MarketChestType;
}

const { chestType } = defineProps<Props>();

const currentChest = ref<MarketChestType>(chestType);
const chests = [
	MarketChestType.COMMON,
	MarketChestType.RARE,
	MarketChestType.LEGENDARY,
];

const getCurrentChestConfig = computed(
	(): EventGrandPrizeTierConfig | undefined => {
		return attributes.value.grand_prizes?.tiers.find(
			(tier: EventGrandPrizeTierConfig): boolean =>
				tier.id === currentChest.value,
		);
	},
);

const switchChest = (direction: "previous" | "next"): void => {
	const currentIndex = chests.indexOf(currentChest.value);
	const offset = direction === "next" ? 1 : -1;

	const newIndex = (currentIndex + offset + chests.length) % chests.length;

	currentChest.value = chests[newIndex];
};
</script>

<style lang="scss" scoped>
.event-possible-rewards-popup {
	@include flex-col-center;
	padding: 0.5rem;
	:deep(.scrollbar__thumb.scrollbar__thumb--horizontal) {
		display: none;
	}
}
.possible-rewards {
	&-title {
		@include flex-col-center;
		position: relative;
		margin: 0 auto;
		margin-top: 0.5rem;
		margin-bottom: 1rem;
		border-width: 0.125rem;
		border-style: solid;
		border-image-source: linear-gradient(
			90deg,
			rgba(205, 145, 45, 0) 0%,
			rgba(205, 145, 45, 0.8) 25%,
			rgba(205, 145, 45, 0.8) 75%,
			rgba(205, 145, 45, 0) 100%
		);
		border-image-slice: 1;
		padding: 0.6rem;
		width: 90%;
		color: #fff3dd;
		&-main {
			color: #f4d278;
			font-weight: bold;
			font-size: 1.4rem;
			line-height: 2rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
		&-sub {
			font-size: 0.906;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
		&-common {
			background-image: linear-gradient(
				90deg,
				rgba(67, 103, 30, 0) 0%,
				rgba(67, 103, 30, 0.8) 25%,
				rgba(67, 103, 30, 0.8) 75%,
				rgba(67, 103, 30, 0) 100%
			);
		}
		&-rare {
			background-image: linear-gradient(
				90deg,
				rgba(15, 65, 144, 0) 0%,
				rgba(15, 65, 144, 0.8) 25%,
				rgba(15, 65, 144, 0.8) 75%,
				rgba(15, 65, 144, 0) 100%
			);
		}
		&-legendary {
			background-image: linear-gradient(
				90deg,
				rgba(116, 35, 148, 0) 0%,
				rgba(116, 35, 148, 0.8) 25%,
				rgba(116, 35, 148, 0.8) 75%,
				rgba(116, 35, 148, 0) 100%
			);
		}
	}
	&-navigation {
		@include flex-row-center;
		gap: 3rem;
		&-button {
			display: flex;
			position: relative;
			justify-content: center;
			align-items: center;
			cursor: pointer;
			box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			background-image: linear-gradient(to top, #181a1d, #303239);
			width: 2.5rem;
			height: 2.5rem;
			pointer-events: all;
		}
	}
	&-info {
		width: 100%;
		color: #372615;
		font-size: 0.813rem;
		text-align: center;
	}
	&-drop {
		&-title {
			margin: 0.4rem 0 0.2rem 0;
			@include flex-row-center;
			flex-wrap: nowrap;
			gap: 0.313rem;
			width: 95%;
			color: #372615;
			font-weight: bold;
			font-size: 0.906rem;
			&-divider {
				height: 0.5rem;
			}
			&-text {
				white-space: nowrap;
			}
		}
		&-rates {
			@include flex-col-start;
			gap: 1rem;
			margin: 0 auto;
			padding-top: 0.5rem;
			padding-bottom: 2em;
			width: 97%;
			max-height: 20rem;
		}
	}
}
</style>
